import { ref } from 'vue'
import { router } from '@inertiajs/vue3'

/**
 * A crossfade for filtered result lists.
 *
 * The first attempt keyed the grid on the filter string so Vue would replace it
 * and the cards would animate in again. That produced two faults: the whole list
 * flashed even when the results were identical, and the staggered entrance ran
 * on every keystroke-sized change, which reads as jitter rather than motion.
 *
 * This drives the transition from the request instead. The list dims and settles
 * a few pixels while the server is answering, then comes back — so the movement
 * means "these results are being replaced", and when nothing changes there is
 * nothing to see beyond a brief dim.
 *
 * `preserveScroll` keeps the page where it is: the filters are usually above the
 * results and jumping to the top after every tick loses your place.
 *
 * @param {string} url
 * @returns {{pending: import('vue').Ref<boolean>, visit: (params: object, options?: object) => void}}
 */
export function useFilterTransition (url) {
  const pending = ref(false)

  const visit = (params, options = {}) => {
    router.get(url, params, {
      preserveState: true,
      preserveScroll: true,
      replace: true,
      ...options,
      onStart: () => { pending.value = true },
      onFinish: () => { pending.value = false },
    })
  }

  return { pending, visit }
}

export default useFilterTransition
